import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";
import { RotatingLines } from "react-loader-spinner";
import { serverPath } from "../../../config/key";
import { authenticatedRequest } from "../../../config/axios";

const IDF_REPORT_REQUEST_TIMEOUT_MS = 120000;

const parseIdfReportError = (error) => {
  let message = null;
  let errorCode = null;
  const data = error?.response?.data;

  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder("utf-8").decode(data);
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.msg || null;
      errorCode = parsed?.errorCode || null;
    } catch {
      // body wasn't JSON
    }
  } else if (data && typeof data === "object") {
    message = data?.message || data?.msg || null;
    errorCode = data?.errorCode || null;
  }

  return { message, errorCode };
};

const IDFReport = ({ setTabDisable }) => {
  const { t } = useTranslation();
  const lang = localStorage.getItem("lang");

  const [fileType, setFileType] = useState("");
  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [isPayment, setIsPayment] = useState(false);
  const [reportType, setReportType] = useState("payment");

  const genereateIDFReport = async () => {
    if (validation()) {
      setIsLoader(true);
      setTabDisable(true);

      try {
        var myDate = toDate;
        var updateEndDate = new Date(myDate.setDate(myDate.getDate() + 1));
        myDate.setDate(myDate.getDate() - 1);

        const response = await authenticatedRequest({
          method: "get",
          url: `${serverPath}/admin/reports?startDate=${new Date(
            fromDate
          )}&endDate=${updateEndDate}&fileType=${fileType}&reportType=${reportType}`,
          responseType: "arraybuffer",
          timeout: IDF_REPORT_REQUEST_TIMEOUT_MS,
        });
        const outputFilename = `idf-report-${Date.now()}.${fileType || "xlsx"}`;
        const url = URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement("a");
        fileLink.href = url;
        fileLink.setAttribute("download", outputFilename);
        fileLink.setAttribute("target", "_blank");
        document.body.appendChild(fileLink);
        fileLink.click();
        fileLink.remove();
        URL.revokeObjectURL(url);
        setIsLoader(false);
        setTabDisable(false);
        setFromDate("");
        setToDate("");
        setFileType("");
      } catch (error) {
        setIsLoader(false);
        setTabDisable(false);

        const { message: backendMessage, errorCode: backendErrorCode } =
          parseIdfReportError(error);

        const isTimeout =
          error?.code === "ECONNABORTED" ||
          /timeout/i.test(String(error?.message || ""));
        const status = error?.response?.status;
        const isGatewayOrProxyTimeout =
          status === 504 || status === 524 || status === 502;

        let message =
          backendMessage || "Some error occurred. Please try again.";
        if (isTimeout) {
          message =
            "The download timed out. Try a shorter date range. If it keeps failing, the API or load balancer may need a higher read timeout for large full reports.";
        } else if (isGatewayOrProxyTimeout) {
          message =
            "The server or proxy stopped waiting while this report was generated (common for large full exports). Try a shorter date range, or raise proxy/API read timeouts behind the scenes.";
        }

        if (
          !isTimeout &&
          !isGatewayOrProxyTimeout &&
          backendErrorCode === "IDF_REPORT_NO_MEETINGS" &&
          backendMessage
        ) {
          toast.info(backendMessage);
        } else {
          toast.error(message);
        }
        console.log("error==>", error);
      }
    }
  };

  const validation = () => {
    if (!fromDate) {
      toast.error(t("From Date is mandatory field"));
      return false;
    }
    if (!toDate) {
      toast.error(t("To Date is mandatory field"));
      return false;
    }
    if (toDate && fromDate && new Date(toDate) < new Date(fromDate)) {
      toast.error(t("End date must be on or after start date"));
      return false;
    }
    return true;
  };

  const handleFileType = (e) => {
    setFileType(e.target.value);
  };

  const handleChange = async (name, value) => {
    if (name === "fromDate") {
      if (!value) {
        setFromDate(null);
      }
      setFromDate(value);
    }
    if (name === "toDate") {
      if (!value) {
        setToDate(null);
      }
      setToDate(value);
    }
  };

  const handleReportType = (event) => {
    const { name, checked } = event.target;

    if (checked) {
      setReportType("full");
    } else {
      setReportType("payment");
    }

    if (name === "reportType") {
      setIsPayment((prev) => !prev);
    }
  };

  return (
    <div className="idf-report">
      <div className="idf-upload-loader">
        <RotatingLines
          strokeColor="grey"
          strokeWidth="5"
          animationDuration="0.75"
          width="72"
          visible={isLoader}
        />
      </div>
      <Form>
        <div className="idf-report-grid ">
          <Form.Group className="form-group">
            <Form.Label>{t("From Date")}</Form.Label>
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={fromDate}
              name="fromDate"
              onChange={(event) => handleChange("fromDate", event)}
              placeholderText="DD/MM/YYYY"
              className="datePicker"
              endDate={new Date()}
              maxDate={new Date()}
              disabled={isLoader}
            />
          </Form.Group>
          <Form.Group className="form-group">
            <Form.Label>{t("To Date")}</Form.Label>
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={toDate}
              name="toDate"
              onChange={(event) => handleChange("toDate", event)}
              placeholderText="DD/MM/YYYY"
              endDate={new Date()}
              maxDate={new Date()}
              className="datePicker"
              popperPlacement="top-start"
              disabled={isLoader}
            />
          </Form.Group>
          <Form.Group className="form-group">
            <Form.Label>{t("Select File Type")}</Form.Label>
            <select
              name="fileType"
              value={fileType}
              onChange={(e) => handleFileType(e)}
              className="form-type form-control"
              disabled={isLoader}
            >
              <option value="">Select</option>
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
          </Form.Group>
          <Form.Group className="form-group report-toggle">
            <Form.Label className={`id-label ${isPayment}`}>
              {" "}
              <p className={` ${isPayment ? "label-active" : ""}`}>
                {" "}
                {t("Payment")}
              </p>
              <div
                className={`slideThree ${isPayment} ${lang}`}
                data-translation={!isPayment ? t("Full") : t("Payment")}
              >
                <input
                  type="checkbox"
                  value="None"
                  id="slideThree"
                  name="reportType"
                  checked={isPayment}
                  onChange={handleReportType}
                  disabled={isLoader}
                />
                <label for="slideThree"></label>
              </div>
              <p className={`${!isPayment ? "label-active" : ""}`}>
                {" "}
                {t("Full")}
              </p>
            </Form.Label>
          </Form.Group>
          <div className="submit-btn-grid">
            <Button
              onClick={genereateIDFReport}
              disabled={isLoader}
              className="download_report"
            >
              {t("Download Report")}
            </Button>
          </div>
        </div>
        {isLoader && reportType === "full" && (
          <p className="idf-report-loading-hint">
            {t(
              "Generating the full report. This can take up to a minute for larger date ranges."
            )}
          </p>
        )}
      </Form>
    </div>
  );
};
export default IDFReport;
